"use client";

import {
  Checkbox,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import type { INFORMED_CONSENT_FORM_GENERATOR_STRINGS } from "@/components/tools/informed-consent-form-generator";
import {
  type FieldSpec,
  SpecFields,
} from "@/components/tools/research-workflow/form-parts";
import type { ConsentInput } from "@/lib/tool-kit/research-workflow/consent";

/*
 * The input panels of the informed consent form generator — six groups of fields, in
 * the order the form itself is read. Split from the widget so the widget file stays its
 * strings, its state and its results; every word here comes from those strings.
 */

export type ConsentStrings = typeof INFORMED_CONSENT_FORM_GENERATOR_STRINGS;
type Strings = ConsentStrings;
type Spec = FieldSpec<ConsentInput, Strings>;

const STUDY_FIELDS: Spec[] = [
  ["title", "titleLabel", "titlePlaceholder", false],
  ["researcher", "researcherLabel", "researcherPlaceholder", false],
  ["role", "roleLabel", "rolePlaceholder", false],
  ["institution", "institutionLabel", "institutionPlaceholder", false],
  ["contact", "contactLabel", "contactPlaceholder", false],
  ["advisor", "advisorLabel", "advisorPlaceholder", false],
  ["irbName", "irbLabel", "irbPlaceholder", false],
  ["irbContact", "irbContactLabel", "irbContactPlaceholder", false],
  ["protocol", "protocolLabel", null, false],
];

const PROCEDURE_FIELDS: Spec[] = [
  ["purpose", "purposeLabel", "purposePlaceholder", true],
  ["procedures", "proceduresLabel", "proceduresPlaceholder", true],
  ["duration", "durationLabel", "durationPlaceholder", false],
  ["experimental", "experimentalLabel", "experimentalPlaceholder", true],
];

const RISK_FIELDS: Spec[] = [
  ["risks", "risksLabel", "risksPlaceholder", true],
  ["benefits", "benefitsLabel", "benefitsPlaceholder", true],
  ["alternatives", "alternativesLabel", "alternativesPlaceholder", true],
];

const EXTRA_FIELDS: Spec[] = [
  ["payment", "paymentLabel", "paymentPlaceholder", true],
  ["costs", "costsLabel", "costsPlaceholder", false],
  ["withdrawal", "withdrawalLabel", "withdrawalPlaceholder", true],
  ["termination", "terminationLabel", "terminationPlaceholder", false],
  ["results", "resultsLabel", "resultsPlaceholder", true],
];

export type ConsentSetter = <K extends keyof ConsentInput>(
  key: K,
  value: ConsentInput[K]
) => void;

export function ConsentFields({
  input,
  set,
  strings,
  count,
  onCount,
  date,
  onDate,
}: {
  input: ConsentInput;
  set: ConsentSetter;
  strings: Strings;
  /** The participant count as typed; parsed by the widget. */
  count: string;
  onCount: (value: string) => void;
  /** The version date as `YYYY-MM-DD`. */
  date: string;
  onDate: (value: string) => void;
}) {
  const specimens = input.dataKind === "specimens";
  return (
    <>
      <Panel title={strings.studyPanel}>
        <div className="grid gap-4 sm:grid-cols-2">
          <SpecFields
            input={input}
            onChange={set}
            prefix="icf"
            specs={STUDY_FIELDS}
            strings={strings}
          />
          <TextField
            id="icf-date"
            label={strings.versionLabel}
            onChange={onDate}
            type="date"
            value={date}
          />
        </div>
      </Panel>
      <Panel title={strings.proceduresPanel}>
        <Notice>{strings.firstSentenceHint}</Notice>
        <SpecFields
          input={input}
          onChange={set}
          prefix="icf"
          specs={PROCEDURE_FIELDS}
          strings={strings}
        />
        <TextField
          id="icf-participants"
          label={strings.participantsLabel}
          numeric
          onChange={onCount}
          value={count}
        />
      </Panel>
      <Panel title={strings.risksPanel}>
        <PillGroup
          label={strings.riskLevelLabel}
          name="icf-risk-level"
          onChange={(value) => set("riskLevel", value)}
          options={[
            { value: "minimal", label: strings.riskMinimal },
            { value: "greater", label: strings.riskGreater },
          ]}
          value={input.riskLevel}
        />
        <SpecFields
          input={input}
          onChange={set}
          prefix="icf"
          specs={RISK_FIELDS}
          strings={strings}
        />
        {input.riskLevel === "greater" ? (
          <SpecFields
            input={input}
            onChange={set}
            prefix="icf"
            specs={[["injury", "injuryLabel", "injuryPlaceholder", true]]}
            strings={strings}
          />
        ) : null}
        <Checkbox
          checked={input.unforeseeable}
          label={strings.unforeseeableCheck}
          onChange={(value) => set("unforeseeable", value)}
        />
        <Checkbox
          checked={input.pregnancy}
          label={strings.pregnancyCheck}
          onChange={(value) => set("pregnancy", value)}
        />
      </Panel>
      <Panel title={strings.privacyPanel}>
        <SpecFields
          input={input}
          onChange={set}
          prefix="icf"
          specs={[
            [
              "confidentiality",
              "confidentialityLabel",
              "confidentialityPlaceholder",
              true,
            ],
          ]}
          strings={strings}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="icf-data-kind"
            label={strings.dataKindLabel}
            onChange={(value) => set("dataKind", value)}
            options={[
              { value: "information", label: strings.dataInformationOption },
              { value: "specimens", label: strings.dataSpecimensOption },
            ]}
            value={input.dataKind}
          />
          <SelectField
            id="icf-future-use"
            label={strings.futureUseLabel}
            onChange={(value) => set("futureUse", value)}
            options={[
              { value: "will-not", label: strings.futureWillNotOption },
              { value: "may", label: strings.futureMayOption },
              { value: "none", label: strings.futureNoneOption },
            ]}
            value={input.futureUse}
          />
          {specimens ? (
            <SelectField
              id="icf-profit"
              label={strings.profitLabel}
              onChange={(value) => set("profit", value)}
              options={[
                { value: "omit", label: strings.omitOption },
                { value: "will", label: strings.profitWillOption },
                { value: "will-not", label: strings.profitWillNotOption },
              ]}
              value={input.profit}
            />
          ) : null}
          {specimens ? (
            <SelectField
              id="icf-genome"
              label={strings.genomeLabel}
              onChange={(value) => set("genome", value)}
              options={[
                { value: "omit", label: strings.omitOption },
                { value: "will", label: strings.genomeWillOption },
                { value: "might", label: strings.genomeMightOption },
              ]}
              value={input.genome}
            />
          ) : null}
        </div>
      </Panel>
      <Panel title={strings.extrasPanel}>
        <SpecFields
          input={input}
          onChange={set}
          prefix="icf"
          specs={EXTRA_FIELDS}
          strings={strings}
        />
        <Checkbox
          checked={input.newFindings}
          label={strings.newFindingsCheck}
          onChange={(value) => set("newFindings", value)}
        />
        <Checkbox
          checked={input.students}
          label={strings.studentsCheck}
          onChange={(value) => set("students", value)}
        />
        <SelectField
          id="icf-recording"
          label={strings.recordingLabel}
          onChange={(value) => set("recording", value)}
          options={[
            { value: "none", label: strings.recordingNone },
            { value: "audio", label: strings.recordingAudioOption },
            { value: "video", label: strings.recordingVideoOption },
            { value: "both", label: strings.recordingBothOption },
          ]}
          value={input.recording}
        />
      </Panel>
      <Panel title={strings.signingPanel}>
        <PillGroup
          label={strings.documentationLabel}
          name="icf-documentation"
          onChange={(value) => set("documentation", value)}
          options={[
            { value: "signed", label: strings.documentationSigned },
            { value: "implied", label: strings.documentationImplied },
          ]}
          value={input.documentation}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="icf-age"
            label={strings.ageLabel}
            numeric
            onChange={(value) => set("minimumAge", value)}
            value={input.minimumAge}
          />
          {input.documentation === "implied" ? (
            <TextField
              id="icf-implied-action"
              label={strings.impliedActionLabel}
              onChange={(value) => set("impliedAction", value)}
              placeholder={strings.impliedActionPlaceholder}
              value={input.impliedAction}
            />
          ) : null}
        </div>
        {input.documentation === "implied" ? (
          <Notice>{strings.impliedNote}</Notice>
        ) : (
          <div className="space-y-2">
            <Checkbox
              checked={input.lar}
              label={strings.larCheck}
              onChange={(value) => set("lar", value)}
            />
            <Checkbox
              checked={input.witness}
              label={strings.witnessCheck}
              onChange={(value) => set("witness", value)}
            />
          </div>
        )}
      </Panel>
    </>
  );
}
